import React, { useEffect, useState, useRef, useCallback } from "react";
import { Route, Switch, Redirect } from "react-router-dom";

import Footer from "./_components/footer";
import Header from "./_components/header";
import Loader from "../../components/main/loader";

import Home from "./home";
import Settings from "./settings";

import { useAuthStore } from "../../store/auth";
import { useMetatagStore } from "../../store/metatag";
import { useUserDataStore } from "../../store/userData";
import { useClientsDataStore } from "../../store/clientsData";
import { useDatasetsStore } from "../../store/datasets";
import { useLayoutStore } from "../../store/layout";
import { useUpdateDataStore } from "../../store/updateData";
import { useSocketDataStore } from "../../store/socketData";

import fetch from "../../utils/fetch";
import Cookies from "../../utils/cookies";

const metatags = require("../../datasets/metatags.json");

interface Props {}

const Dashboard: React.FC<Props> = () => {
    const [loading, setLoading] = useState(true);
    const metatag = useMetatagStore<any>((s) => s.metatag);
    const setMetatag = useMetatagStore<any>((s) => s.setMetatag);
    const setloggedIn = useAuthStore<any>((s) => s.setloggedIn);
    const setUser = useUserDataStore<any>((s) => s.setUser);
    const user = useUserDataStore<any>((s) => s.user);
    const setClients = useClientsDataStore<any>((s) => s.setClients);
    const setUpdateData = useUpdateDataStore<any>((s) => s.setUpdateData);
    const setSocketCallQueue = useSocketDataStore<any>((s) => s.setSocketCallQueue);
    const setSocketCallStatus = useSocketDataStore<any>((s) => s.setSocketCallStatus);
    const setBhvmTickets = useSocketDataStore<any>((s) => s.setBhvmTickets);
    const [loadingStep, setLoadingStep] = useState<number>(1);
    const [isVisible, setIsVisible] = useState(document.visibilityState === "visible");
    const isVisibleRef = useRef<any>(null);

    const updateData = useUpdateDataStore<any>((s) => s.updateData);

    const heartbeat = async () => {
        await new fetch("/user/vca/heartbeat", {}, { success: false, error: false }).get();
    };

    const get_data = async () => {
        const loading_time = new Date().getTime();
        setLoadingStep(1);
        const auth = await new fetch(null, {}, { success: false, error: true }).auth();
        if (!auth || auth.status !== "success") {
            setloggedIn(false);
            window.location.href = "/login";
            return;
        }
        if (auth.status === "success" && auth.auth.logged_in === true) {
            setloggedIn(true);
        }
        setLoadingStep(2);
        const user_data = await new fetch("/user/vca/data/user", {}, { success: false, error: false }).get();
        if (!user_data || user_data.status !== "success") {
            setloggedIn(false);
            window.location.href = "/login";
            return;
        }
        if (user_data.status === "success") {
            const _data_user = user_data.data;

            setLoadingStep(3);
            const user_device_token = await new fetch("/user/vca/twilio/device-token", {}, { success: false, error: false }).get();
            if (!user_device_token || user_device_token.status !== "success") {
                setloggedIn(false);
                window.location.href = "/login";
                return;
            }
            if (user_device_token.status === "success") {
                const data = user_device_token.data;
                setUser({ ..._data_user, device_token: data.device_token });
            }
        }
        setLoadingStep(4);
        const client_data = await new fetch("/user/vca/data/client", {}, { success: false, error: false }).get();
        if (!client_data || client_data.status !== "success") {
            setloggedIn(false);
            window.location.href = "/login";
            return;
        }
        if (client_data.status === "success") {
            const data = client_data.data;
            setClients(data);
        }
        setLoadingStep(5);

        setLoading(false);
        // console.log(Number(Number(new Date().getTime() - loading_time) / 1000) + "s");
    };

    const update_data = async () => {
        let user_data, clients_data;

        if (updateData.some((item: any) => ["all", "user"].includes(item))) user_data = await new fetch("/user/vca/data/user", {}, { success: false, error: false }).get();
        if (updateData.some((item: any) => ["all", "clients"].includes(item))) clients_data = await new fetch("/user/vca/data/client", {}, { success: false, error: false }).get();

        if (user_data?.status === "success") setUser({ ...user, ...user_data.data });
        if (clients_data?.status === "success") setClients([...clients_data.data]);

        setUpdateData([]);
    };

    const wsRefs = useRef<{ [key: string]: WebSocket | null }>({});

    // FIX 2: Track auto-close timers per path so they can be cleared before reconnecting,
    // preventing two timers from racing against the same socket path.
    const wsTimerRefs = useRef<{ [key: string]: ReturnType<typeof setTimeout> | null }>({});

    // FIX 2 (backoff): Track consecutive failed reconnect attempts per path so we can
    // apply exponential backoff instead of always retrying after a flat 1 second.
    const wsRetryRefs = useRef<{ [key: string]: number }>({});

    // FIX 3 & 4: Wrapped in useCallback with stable [] deps so that:
    //   - A single stable function reference is created for the component lifetime.
    //   - The onclose handler always calls the same _ws_handler, not a stale render-scope copy.
    //   All dependencies (_ws refs, Zustand setters) are stable by nature, so [] is correct.
    const _ws_handler = useCallback((path = "") => {
        if (path === "") return;

        // Clear any pending auto-close timer for this path before opening a new connection
        if (wsTimerRefs.current[path]) {
            clearTimeout(wsTimerRefs.current[path]!);
            wsTimerRefs.current[path] = null;
        }

        // Close any existing connection for this path before opening a new one
        if (wsRefs.current[path]) {
            wsRefs.current[path]!.onclose = null; // prevent auto-reconnect
            wsRefs.current[path]!.close();
        }

        const _ws = new WebSocket(`wss://ws-vca.ypc.solutions/user/twilio/${path}`);
        wsRefs.current[path] = _ws;
        const cookie = new Cookies();

        _ws.onopen = (e) => {
            // FIX 3: Guard against stale sockets — if this socket has already been replaced,
            // do not send auth or schedule the auto-close timer.
            if (_ws.readyState === WebSocket.OPEN && wsRefs.current[path] === _ws) {
                // console.log("ws connected");
                // Successful connection — reset the backoff counter for this path
                wsRetryRefs.current[path] = 0;
                _ws.send(
                    JSON.stringify({
                        Authorization: cookie.get("token"),
                        xAccessToken: cookie.get("access_token"),
                    }),
                );
                // This timer forces a periodic connection refresh so the auth token stays
                // current. It bypasses the error-recovery path entirely by nulling onclose
                // before closing, then calling _ws_handler directly — so the retry counter
                // is never incremented and no backoff delay is applied to a healthy refresh.
                wsTimerRefs.current[path] = setTimeout(
                    () => {
                        wsTimerRefs.current[path] = null;
                        _ws.onclose = null; // bypass error recovery — this is an intentional refresh
                        _ws.close();
                        _ws_handler(path); // reconnect immediately with a fresh token
                    },
                    10 * 60 * 1000,
                );
            }
        };
        _ws.onmessage = (e) => {
            const res = JSON.parse(e.data);
            const status = res.status;
            const data = res.data;

            if (status === "success") {
                if (path === "call-status") {
                    setSocketCallStatus(data);
                }

                if (path === "call-queue-v2") {
                    setSocketCallQueue(data);
                }
                if (path === "bhvm-tickets") {
                    setBhvmTickets(data);
                }
            }
        };
        _ws.onerror = (e) => {
            console.error("webSocket error:", e);
        };
        _ws.onclose = (e) => {
            // console.log("webSocket connection closed");
            if (wsRefs.current[path] === _ws) {
                // Exponential backoff capped at 30s, plus up to 1s of random jitter.
                // The cap prevents indefinite growth; the jitter breaks lockstep retries
                // among clients that connected at the same time, spreading their reconnects
                // across a ~1s window at each backoff tier instead of all firing at once.
                // The retry counter is reset to 0 on the next successful onopen.
                const retries = wsRetryRefs.current[path] ?? 0;
                const delay = Math.min(1000 * Math.pow(2, retries), 30000);
                const jitter = Math.random() * 1000;
                wsRetryRefs.current[path] = retries + 1;
                // console.log(`webSocket reconnecting in ${Math.round(delay + jitter)}ms (attempt ${retries + 1})`);
                setTimeout(() => {
                    _ws_handler(path);
                }, delay + jitter);
            }
        };
    }, []);

    useEffect(() => {
        _ws_handler("call-status");
        _ws_handler("call-queue-v2");
        _ws_handler("bhvm-tickets");

        return () => {
            // Clear all auto-close timers on unmount
            Object.entries(wsTimerRefs.current).forEach(([, timer]) => {
                if (timer) clearTimeout(timer);
            });
            wsTimerRefs.current = {};
            wsRetryRefs.current = {};

            Object.values(wsRefs.current).forEach((ws) => {
                if (ws) {
                    ws.onclose = null;
                    ws.close();
                }
            });
            wsRefs.current = {};
        };
    }, []);

    useEffect(() => {
        if (JSON.stringify(updateData) !== "[]") {
            update_data();
        }
    }, [updateData]);

    useEffect(() => {
        if (isVisible === true) {
            heartbeat();
            isVisibleRef.current = setInterval(() => {
                heartbeat();
            }, 60 * 1000);
        } else {
            if (isVisibleRef.current) {
                clearInterval(isVisibleRef.current);
            }
        }
    }, [isVisible]);

    useEffect(() => {
        const path = window.location.pathname.replace(/\/$/, "");

        if (path !== metatag?.route) {
            const split = path.split("/");
            if (split.length >= 3 && split[1] === "reset-password" && split[2].length === 36) {
                setMetatag(metatags["/reset-password"]);
            } else {
                setMetatag(metatags[path]);
            }
        }
    });

    useEffect(() => {
        get_data();

        document.body.style.overflowY = "hidden";
    }, []);

    // FIX 1: Extract the handler to a stable named reference so removeEventListener
    // actually removes the correct listener (anonymous functions never match).
    useEffect(() => {
        const handleVisibilityChange = () => {
            setIsVisible(document.visibilityState === "visible");
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, []);

    useEffect(() => {
        const handleBeforeUnload = (event: any) => {
            event.preventDefault();
            event.returnValue = ""; // Some browsers require this line for the warning to show.
        };

        window.addEventListener("beforeunload", handleBeforeUnload);

        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, []);

    if (loading === true) {
        return (
            <>
                <div id="page-loading">
                    <Loader type="circle" />
                    <small style={{ position: "absolute", top: "30px", left: "30px" }}>{Math.ceil((loadingStep / 6) * 100)}%</small>
                </div>
            </>
        );
    }

    if (user.account_status === 0) return <></>;

    return (
        <>
            <div id="dashboard">
                <Header />
                <div id="dashboard-main-view" className={["AI chat history", "Email inbox"].includes(metatag?.page) ? "dashboard-main-view-expanded" : ""}>
                    <Switch>
                        <Route path="/dashboard" render={() => <Home />} exact />
                        <Route path="/dashboard/settings" render={() => <Settings />} exact />
                        <Redirect to="/404" />
                    </Switch>

                    {<Footer hide={["Messages", "Csalendar"].includes(metatag?.page)} />}
                </div>
            </div>
        </>
    );
};

export default Dashboard;
